import { createSlice } from "@reduxjs/toolkit";
import { set } from "lodash";
// ⚠️ `userLogout` / `supportLogout` exist ONLY in LoginSlice/index.desktop.ts — hazard 6.
import { supportLogout, userLogout } from "../LoginSlice";
// ⚠️ `isAndroid` is named at its variant on purpose. It is the QUIET form of hazard 16 in
// waiting: mobile's `utils/nativeHooks` has a same-named function with a different body
// (`return true;`) which is simply not in core yet — the day it arrives, a bare folder import
// here would silently flip web's behaviour. Naming the variant now costs nothing.
import { isAndroid } from "../../../utils/nativeHooks";
import { MASTER_DATA_PICKLIST_TYPES } from "../../../enums/labelItemSettings";
import { sharedInitialState, sharedReducers } from "./shared";
import type { SharedBusinessState } from "./shared";

/**
 * ⚠️ `packages/core`'s tsconfig has no `DOM` lib (it must stay RN-safe), so the global is
 * declared locally — same treatment as `helpers/commonHelpers/index.desktop.ts`. The clone is
 * what keeps the `set()` calls below from mutating the caller's payload.
 */
declare function structuredClone<T>(value: T): T;

/**
 * Web adds no FIELDS to the shared shape — both apps declare the same eight — but it does
 * re-spell two of them; see below. The name is kept because the app file exported
 * `BusinessState`, and the shim must re-export that exact name.
 *
 * ⚠️ An empty `interface … extends`, NOT `type X = SharedX` — ledger hazard 11: `--verify` inlines
 * `extends` and compares by member set, but does not follow a type ALIAS, so the alias form fails
 * with "types: body changed". Match the app's declaration KIND; both apps used an interface.
 */
export interface BusinessState extends SharedBusinessState {
  // Re-declared with WEB's spelling; `shared.ts` holds these two as `any` precisely so each
  // platform can keep its own. Mobile spells them `Record<string, any>`.
  businessData: {},
  userData: {},
}

const initialState: BusinessState = {
  ...sharedInitialState,
};

export const businessSlice = createSlice({
  name: 'business',
  initialState,
  reducers: {
    ...sharedReducers,
    // ── Diverges by ARITY only (hazard 14): web dispatches this WITH a payload, mobile does
    //    not declare the parameter. Runtime-identical; the signature is what matters.
    getBusinessDataObj: (state, action) => {
      state.isBusinessDataFetching = true;
    },
    // ── Genuinely divergent: mobile assigns the payload straight through. Web scrubs four
    //    `billTemplate` fields when running inside the Android WebView wrapper, because that
    //    shell cannot render a QR code, an on-open footer, or a signature bitmap.
    getBusinessDataObjSuccess: (state, action) => {
      state.isBusinessDataFetching = false;
      let dataToUpdate = structuredClone(action.payload || {});
      if (isAndroid()) {
        set(dataToUpdate, "billTemplate.printQRCode", false);
        set(dataToUpdate, "billTemplate.footerOnOpen", false);
        set(dataToUpdate, "billTemplate.signatureImageId", "");
        set(dataToUpdate, "billTemplate.signatureBitmap", null);
      }
      state.businessData = dataToUpdate;
    },
    // ── The 5 below are web-only; mobile's extras are `getUserDataObjOnly` and
    //    `updateUserSaleLicence`.
    businessDataCallComplete: (state) => {
      // this action is to tell that the business data call has completed
    },
    userDataCallComplete: (state) => {
    // this action is to tell that the user data call has completed
    },
    resetBusinessData: (state) => {
      state.businessData = {}
    },
    updateBusinessSlice: (state, action) => {
      return action.payload;
    },
    getMasterDataPicklist: (
      state,
      action: {
        payload: {
        callback: (pickList: any[]) => void;
        listType: MASTER_DATA_PICKLIST_TYPES;
        };
      }
      ) => {},
  },
  // ⚠️ WEB-ONLY. Mobile's BusinessSlice declares no extraReducers at all.
  //    Note the asymmetry between the two cases: `supportLogout` ALSO clears `businessData`,
  //    `userLogout` does not.
  extraReducers: (builder) => {
    builder
      .addCase(userLogout, (state) => {
        state.isUserDataFetching = false;
        state.userData = {};
      })
      .addCase(supportLogout, (state) => {
        state.isUserDataFetching = false;
        state.userData = {};
        state.businessData = {};
      });
  },
})

export const {
  getBusinessDataObj,
  getBusinessDataObjFailure,
  getBusinessDataObjSuccess,
  getUserDataObj,
  getUserDataObjSuccess,
  getUserDataObjFailure,
  updateUserLicence,
  getPermittedLicences,
  setIsOffline,
  updateBusinessSlice,
  resetBusinessData,
  getMasterDataPicklist,
  userDataCallComplete,
  businessDataCallComplete,
} = businessSlice.actions;
export default businessSlice.reducer;
